import { useState } from "react";

import { Film, Images, RadioTower, ShieldCheck, Video } from "lucide-react";

import { CreatorAccessSection } from "../components/CreatorAccessSection.jsx";

import { useCreatorVideoAccess } from "../hooks/useCreatorVideoAccess.js";
import { useCreatorAlbumAccess } from "../hooks/useCreatorAlbumAccess";
import { useCreatorStreamAccess } from "../hooks/useCreatorStreamAccess";
import { useCreatorPopAccess } from "../hooks/useCreatorPopAccess";

import "./CreatorAccessView.css";

const TAB_CONFIG = {
  video: {
    label: "Videos",
    itemSingular: "video",
    itemIdKey: "videoId",
    grantIdsKey: "videoIds",
  },

  album: {
    label: "Albums",
    itemSingular: "album",
    itemIdKey: "albumId",
    grantIdsKey: "albumIds",
  },

  stream: {
    label: "Streams",
    itemSingular: "stream",
    itemIdKey: "streamSessionId",
    grantIdsKey: "streamSessionIds",
  },

  pop: {
    label: "Pops",
    itemSingular: "Pop",
    itemIdKey: "popId",
    grantIdsKey: "popIds",
  },
};

function VideoAccess({ username, userId }) {
  const resource = useCreatorVideoAccess(username, userId);

  return <CreatorAccessSection resource={resource} config={TAB_CONFIG.video} />;
}

function AlbumAccess({ username, userId }) {
  const resource = useCreatorAlbumAccess(username, userId);

  return <CreatorAccessSection resource={resource} config={TAB_CONFIG.album} />;
}

function StreamAccess({ username, userId }) {
  const resource = useCreatorStreamAccess(username, userId);

  return (
    <CreatorAccessSection resource={resource} config={TAB_CONFIG.stream} />
  );
}

function PopAccess({ username, userId }) {
  const resource = useCreatorPopAccess(username, userId);

  return <CreatorAccessSection resource={resource} config={TAB_CONFIG.pop} />;
}

const tabs = [
  {
    id: "video",
    label: "Videos",
    Icon: Video,
    Component: VideoAccess,
  },
  {
    id: "album",
    label: "Albums",
    Icon: Images,
    Component: AlbumAccess,
  },
  {
    id: "stream",
    label: "Streams",
    Icon: RadioTower,
    Component: StreamAccess,
  },
  {
    id: "pop",
    label: "Pops",
    Icon: Film,
    Component: PopAccess,
  },
];

export default function CreatorFreeAccessView({ username, userId }) {
  const [tab, setTab] = useState("video");

  const selected = tabs.find((item) => item.id === tab) || tabs[0];

  const Section = selected.Component;

  return (
    <section className="creator-free-access">
      <header className="cfa-header">
        <span className="cfa-eyebrow">
          <ShieldCheck size={15} />
          Creator permissions
        </span>

        <h1>Grant Access</h1>

        <p>
          Manage who can view your content. Each type has its own access
          settings.
        </p>
      </header>

      <nav className="cfa-tabs" aria-label="Content access type">
        {tabs.map(({ id, label, Icon }) => (
          <button
            type="button"
            key={id}
            className={`cfa-tab ${tab === id ? "is-active" : ""}`}
            aria-current={tab === id ? "page" : undefined}
            onClick={() => setTab(id)}
          >
            <Icon size={17} />
            {label}
          </button>
        ))}
      </nav>

      <Section
        key={`${tab}:${username}:${userId}`}
        username={username}
        userId={userId}
      />
    </section>
  );
}
